import type {
  LockerverseSignup,
  LockerverseSignupAddress,
} from "@lockerverse/sdk/signup";
import {
  check,
  nonEmpty,
  nullable,
  object,
  pipe,
  regex,
  safeParse,
  string,
  transform,
  trim,
} from "valibot";
import {
  optionalEmailSchema,
  optionalPhoneSchema,
  requiredEmailSchema,
  requiredPhoneSchema,
} from "./contact-validation.ts";
import {
  type CustomFieldValues,
  reconcileCustomFieldValues,
  validateCustomFields,
} from "./custom-fields.tsx";

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const OPTIONAL_POSITIVE_INTEGER_PATTERN = /^(?:$|[1-9]\d*)$/;

export type SignupAddressValues = Omit<LockerverseSignupAddress, "line2"> & {
  line2: string | null;
};

export type SignupFormValues = {
  address: SignupAddressValues;
  customFields: CustomFieldValues;
  email: string;
  honeypot: string;
  name: string;
  participantCount: string;
  phone: string;
  phoneCountry: string;
};

const nameSchema = pipe(string(), trim(), nonEmpty("Name is required."));
const optionalParticipantCountSchema = pipe(
  string(),
  trim(),
  regex(
    OPTIONAL_POSITIVE_INTEGER_PATTERN,
    "Guest count must be a positive whole number."
  ),
  transform((value) => (value ? Number(value) : null)),
  check(
    (value) => value === null || Number.isSafeInteger(value),
    "Guest count must be a positive whole number."
  )
);
const requiredParticipantCountSchema = pipe(
  string(),
  trim(),
  nonEmpty("Guest count is required."),
  regex(
    POSITIVE_INTEGER_PATTERN,
    "Guest count must be a positive whole number."
  ),
  transform(Number),
  check(
    (value) => Number.isSafeInteger(value),
    "Guest count must be a positive whole number."
  )
);
const addressFieldsSchema = object({
  city: string(),
  country: string(),
  line1: string(),
  line2: nullable(string()),
  postalCode: string(),
  state: string(),
});
const requiredAddressSchema = pipe(
  addressFieldsSchema,
  check(isAddressComplete, "Complete the address.")
);

const EMPTY_ADDRESS: SignupAddressValues = {
  city: "",
  country: "US",
  line1: "",
  line2: null,
  postalCode: "",
  state: "",
};
const optionalAddressSchema = pipe(
  addressFieldsSchema,
  check(
    (address) => isAddressEmpty(address) || isAddressComplete(address),
    "Complete the address or leave it blank."
  )
);

export function isAddressEmpty(address: SignupAddressValues) {
  return ![
    address.city,
    address.line1,
    address.line2,
    address.postalCode,
    address.state,
  ].some((value) => value?.trim());
}

function isAddressComplete(address: SignupAddressValues) {
  return [
    address.city,
    address.country,
    address.line1,
    address.postalCode,
    address.state,
  ].every((value) => value.trim());
}

/** Phone must already be normalized by the shared phone helper when enabled. */
export function validateSignupForm(
  signup: LockerverseSignup,
  values: SignupFormValues
) {
  const normalizedValues = {
    ...values,
    address: signup.fields.address.enabled ? values.address : EMPTY_ADDRESS,
    customFields: reconcileCustomFieldValues(
      signup.customFields,
      values.customFields
    ),
    email: signup.fields.email.enabled ? values.email : "",
    participantCount: signup.fields.participantCount.enabled
      ? values.participantCount
      : "",
    phone: signup.fields.phone.enabled ? values.phone : "",
  };
  const schema = object({
    address:
      signup.fields.address.enabled && signup.fields.address.required
        ? requiredAddressSchema
        : optionalAddressSchema,
    email:
      signup.fields.email.enabled && signup.fields.email.required
        ? requiredEmailSchema
        : optionalEmailSchema,
    name: nameSchema,
    participantCount:
      signup.fields.participantCount.enabled &&
      signup.fields.participantCount.required
        ? requiredParticipantCountSchema
        : optionalParticipantCountSchema,
    phone:
      signup.fields.phone.enabled && signup.fields.phone.required
        ? requiredPhoneSchema
        : optionalPhoneSchema,
  });
  const result = safeParse(schema, normalizedValues);
  const errors = validateCustomFields(
    signup.customFields,
    normalizedValues.customFields
  );
  if (!result.success) {
    for (const issue of result.issues) {
      const field = issue.path?.[0]?.key;
      const fieldName = [
        "address",
        "email",
        "name",
        "participantCount",
        "phone",
      ].find((name) => name === field);
      if (fieldName && !errors[fieldName]) {
        errors[fieldName] = issue.message;
      }
    }
  }
  return result.success
    ? { errors, value: { ...normalizedValues, ...result.output } }
    : { errors, value: null };
}
